import {
  normalizeCmsTypography,
  resolveCmsFontWeight,
  resolvePageBackground,
  resolvePageForeground,
  type CmsPageTextColorMode,
  type CmsTheme,
} from "@/lib/cms/cms-theme.constants";

/**
 * Apparence PAR PAGE — stockée dans `layout.root.props.appearance` du doc Puck.
 * Structurel (identique toutes langues) → JAMAIS extrait en ContentString, donc
 * non touché par dehydrate/hydrate (qui ne ciblent que `content[].props`).
 * Aucune migration : l'API persiste `root.props` en Json opaque.
 *
 * Sémantique IDENTIQUE à l'ancien CMS (cf. `resolvePageBackground` /
 * `resolvePageForeground` / typographie) pour un rendu cohérent BO ↔ front.
 */
export type PageAppearance = {
  pageBackgroundColorId: string | null;
  pageTextColorMode: CmsPageTextColorMode;
  pageTextColorId: string | null;
  pageFontFamily: string | null;
  pageFontVariant: string | null;
};

export const DEFAULT_PAGE_APPEARANCE: PageAppearance = {
  pageBackgroundColorId: null,
  pageTextColorMode: "AUTO",
  pageTextColorId: null,
  pageFontFamily: null,
  pageFontVariant: null,
};

/** Normalise une valeur d'apparence partielle/inconnue (root.props.appearance). */
export function normalizePageAppearance(
  value: Partial<PageAppearance> | null | undefined,
): PageAppearance {
  if (!value || typeof value !== "object") return { ...DEFAULT_PAGE_APPEARANCE };
  // "MANUAL" : valeur hors enum écrite par les premiers imports du shell
  // (en-tête / pied de page) ; elle désignait une couleur de texte choisie.
  const raw: string | null | undefined = value.pageTextColorMode;
  const mode = raw === "MANUAL" ? "CUSTOM" : raw;
  return {
    pageBackgroundColorId: value.pageBackgroundColorId ?? null,
    pageTextColorMode:
      mode === "THEME_TEXT" || mode === "CUSTOM" ? mode : "AUTO",
    pageTextColorId: value.pageTextColorId ?? null,
    pageFontFamily: value.pageFontFamily ?? null,
    pageFontVariant: value.pageFontVariant ?? null,
  };
}

/**
 * Résout l'apparence d'une page en valeurs CSS concrètes pour l'aperçu éditeur.
 * Réutilise EXACTEMENT la logique de l'ancien CMS / du front (`PageProviders`) :
 *  - fond = couleur palette par id, sinon défaut site ;
 *  - texte = AUTO → texte par défaut sinon contraste ; THEME_TEXT → texte du
 *    thème ; CUSTOM → pageTextColorId ;
 *  - typographie = police/variante page sinon défaut site.
 */
export function resolvePageAppearanceStyle(
  theme: CmsTheme | null | undefined,
  appearance: PageAppearance,
): {
  backgroundColor: string;
  color: string;
  fontFamily: string;
  fontWeight: number;
} {
  const backgroundColor = resolvePageBackground(
    theme,
    appearance.pageBackgroundColorId,
  );
  const color = resolvePageForeground(theme, {
    pageBackgroundColorId: appearance.pageBackgroundColorId,
    pageTextColorMode: appearance.pageTextColorMode,
    pageTextColorId: appearance.pageTextColorId,
  });

  const typography = normalizeCmsTypography(
    appearance.pageFontFamily ?? theme?.defaultPageFontFamily ?? null,
    appearance.pageFontVariant ?? theme?.defaultPageFontVariant ?? null,
  );
  const fontFamily = typography.fontFamily ?? "Instrument Sans";
  const fontWeight = resolveCmsFontWeight(fontFamily, typography.fontVariant);

  return { backgroundColor, color, fontFamily, fontWeight };
}

/**
 * Variables CSS du thème de la page (`--cms-page-bg/fg`, lien au survol), lues
 * par les couleurs `{source:'theme'}` et les classes `cms-page-*`. MIROIR du
 * front (server-providers.tsx). Posées sur le canvas ET sur le panneau des
 * champs, pour que les pastilles du sélecteur montrent les vraies couleurs.
 */
export function pageThemeCssVars(
  theme: CmsTheme | null | undefined,
  style: { backgroundColor: string; color: string },
): Record<string, string> {
  return {
    "--cms-page-bg": style.backgroundColor,
    "--cms-page-fg": style.color,
    // Vide → variable non posée → les liens gardent la couleur du texte.
    ...(theme?.defaultPageLinkColor
      ? { "--cms-page-link-color": theme.defaultPageLinkColor }
      : {}),
  };
}
